import React from 'react'

export type FontScaleMode = 'small' | 'medium' | 'large'

type FontScaleContextValue = {
  fontScale: FontScaleMode
  setFontScale: (next: FontScaleMode) => void
  fontSizePx: number
}

const STORAGE_KEY = 'ebookhub:ui-font-scale'

const FONT_SCALE_TO_SIZE: Record<FontScaleMode, number> = {
  small: 15,
  medium: 16,
  large: 18,
}

const FontScaleContext = React.createContext<FontScaleContextValue | null>(null)

/**
 * 读取全站字号偏好，异常时回退到默认中号。
 */
const readStoredFontScale = (): FontScaleMode => {
  try {
    const raw = String(localStorage.getItem(STORAGE_KEY) || '').trim()
    if (raw === 'small' || raw === 'medium' || raw === 'large') {
      return raw
    }
  } catch {
    void 0
  }
  return 'medium'
}

/**
 * 全站字号上下文，统一负责持久化与 DOM 应用，避免各页面重复处理。
 */
export const FontScaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fontScale, setFontScaleState] = React.useState<FontScaleMode>(() => readStoredFontScale())

  const setFontScale = React.useCallback((next: FontScaleMode) => {
    setFontScaleState(next)
  }, [])

  const fontSizePx = FONT_SCALE_TO_SIZE[fontScale] || FONT_SCALE_TO_SIZE.medium

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, fontScale)
    } catch {
      void 0
    }
  }, [fontScale])

  React.useEffect(() => {
    if (typeof document === 'undefined') return
    const html = document.documentElement
    const body = document.body
    html.dataset.uiFontScale = fontScale
    body?.setAttribute('data-ui-font-scale', fontScale)
    html.style.fontSize = `${fontSizePx}px`
    html.style.setProperty('--app-font-size-base', `${fontSizePx}px`)
    html.style.setProperty('--app-font-scale-ratio', String(fontSizePx / FONT_SCALE_TO_SIZE.medium))
  }, [fontScale, fontSizePx])

  const value = React.useMemo<FontScaleContextValue>(() => ({
    fontScale,
    setFontScale,
    fontSizePx,
  }), [fontScale, fontSizePx, setFontScale])

  return (
    <FontScaleContext.Provider value={value}>
      {children}
    </FontScaleContext.Provider>
  )
}

/**
 * 读取并修改全站字号偏好。
 */
export const useFontScale = () => {
  const context = React.useContext(FontScaleContext)
  if (!context) {
    throw new Error('useFontScale must be used within FontScaleProvider')
  }
  return context
}
